// Apolo IDE — client for the Hermes API (/api/hermes).
//
// Thin wrappers over apiFetch (auto-JWT), same idiom as remoteDispatchClient.
// Bodies follow the house convention (raw success payloads; errors as
// `{ error, code }`), so each method unwraps one envelope level. Non-2xx
// throws an Error carrying `.status` + `.code` — callers MUST be able to
// tell a 409 `illegal_transition` (lost CAS claim race) from real failures.
//
// `makeHermesApi({ fetchImpl })` exists for tests (inject a fake apiFetch);
// `hermesApi` is the default instance used by the app. The surface is split
// into small per-domain factories over the SAME injected `unwrap` — one giant
// object stoura o complexity-ratchet (max 80 linhas/função); comportamento
// idêntico, só reorganização.

import { apiFetch } from '../../lib/api.js';
import { redactSecrets } from '../../lib/secretRedaction.js';

/** Build the one-envelope unwrapper over an injected fetch. Non-2xx → throw
 *  carrying .status + .code (callers distinguish 409 illegal_transition etc.). */
function makeUnwrap(fetchImpl) {
  return async function unwrap(path, opts) {
    const res = await fetchImpl(path, opts);
    if (!res.ok) {
      const e = new Error(res.error || `hermes request failed (${res.status})`);
      e.status = res.status;
      e.code = res.code ?? (res.data && typeof res.data === 'object' ? res.data.code : undefined);
      throw e;
    }
    return res.data;
  };
}

// ---- agents (T04) ---------------------------------------------------------
function agentsApi(unwrap) {
  return {
    /** Roster. Backend defaults to including disabled; pass false to filter. */
    async listAgents({ includeDisabled } = {}) {
      const qs = includeDisabled === false ? '?includeDisabled=false' : '';
      const data = await unwrap(`/api/hermes/agents${qs}`, { method: 'GET' });
      return data?.agents || [];
    },

    async createAgent(input) {
      const data = await unwrap('/api/hermes/agents', { method: 'POST', body: input });
      return data?.agent;
    },

    async getAgent(id) {
      const data = await unwrap(`/api/hermes/agents/${id}`, { method: 'GET' });
      return data?.agent;
    },

    async updateAgent(id, patch) {
      const data = await unwrap(`/api/hermes/agents/${id}`, { method: 'PATCH', body: patch });
      return data?.agent;
    },

    async deleteAgent(id) {
      return unwrap(`/api/hermes/agents/${id}`, { method: 'DELETE' });
    },
  };
}

// ---- runs (T05a) + outbound / state / fan-out -----------------------------
function runsApi(unwrap) {
  return {
    /** Enqueue a run (state queued). Returns the run record. */
    async startRun(agentId, task) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/run`, {
        method: 'POST',
        body: { task },
      });
      return data?.run;
    },

    /** Full detail: { run, children, progress }. */
    async getRun(id) {
      return unwrap(`/api/hermes/runs/${id}`, { method: 'GET' });
    },

    /** Board listing. params = { agentId?, state?, rootsOnly?, limit? }. */
    async listRuns(params = {}) {
      const q = new URLSearchParams();
      if (params.agentId != null) q.set('agentId', params.agentId);
      if (params.state != null) q.set('state', params.state);
      if (params.rootsOnly != null) q.set('rootsOnly', String(params.rootsOnly));
      if (params.limit != null) q.set('limit', String(params.limit));
      const qs = q.toString();
      const data = await unwrap(`/api/hermes/runs${qs ? `?${qs}` : ''}`, { method: 'GET' });
      return data?.runs || [];
    },

    /** Cancel (cascades to children). Returns { run, cancelledChildren }. */
    async cancelRun(id) {
      return unwrap(`/api/hermes/runs/${id}/cancel`, { method: 'POST' });
    },

    /**
     * Multi-channel outbound (P4-T02, D20). Ask the backend to post a TERMINAL
     * run's result back to its source channel. The Mac driver calls this after
     * driveRun (the Mac can't decrypt the channel secret — the backend does it
     * server-side). Idempotent (the backend's outbound_at guard) + best-effort
     * for the caller (a 404/409 just means nothing to post). Returns
     * { posted, skipped }.
     */
    async requestOutbound(runId) {
      return unwrap(`/api/hermes/runs/${runId}/outbound`, { method: 'POST' });
    },

    /**
     * Executor state report (claim/finalize). patch = { state?, result?,
     * error?, startedAt?, finishedAt?, tokens?, cost? }. Illegal/concurrent
     * transitions surface as a throw with status 409, code 'illegal_transition'.
     *
     * Gap C (T10): result/error are DURABLE (D1) and re-rendered by the UI;
     * the loop's finalText can echo secrets the model read via tools (the
     * run_command redaction covers tool OUTPUT, not finalText). Masking here
     * — the ONE seam every finalize path crosses (driveRun done/failed, the
     * crash patch, fanOutAndMerge's merged parent) — keeps the application
     * layer free of lib imports (the Hermes architecture gate forbids them).
     */
    async patchRunState(id, patch) {
      const body = { ...patch };
      if (typeof body.result === 'string') body.result = redactSecrets(body.result);
      if (typeof body.error === 'string') body.error = redactSecrets(body.error);
      const data = await unwrap(`/api/hermes/runs/${id}/state`, { method: 'PATCH', body });
      return data?.run;
    },

    /**
     * Fan-out (T06a): CAS the running root → fanning_out + spawn N queued
     * children. subtasks = [string | { task, root? }]. Returns { run, children }
     * (201). CAUTION for callers: a non-201 here means STATE UNKNOWN — a
     * mid-insert crash can leave a fanning_out parent with partial children;
     * re-GET the run and reconcile before deciding anything.
     */
    async fanOut(id, subtasks) {
      return unwrap(`/api/hermes/runs/${id}/fanout`, { method: 'POST', body: { subtasks } });
    },
  };
}

// ---- per-agent memory (P2-T02b, D18) --------------------------------------
//
// Agent memory mirrors code_user_memories + an agent_id. The .app uses
// recallMemory to PREPEND a recalled block to the run prompt (the agent
// "remembers") and appendMemory to record a run-summary on success (the
// agent "learns" between runs). list/update/delete back the MemoryPanel.
function memoryApi(unwrap) {
  return {
    /** List an agent's memories (newest first). Unwraps {memories}. */
    async listMemory(agentId, { limit } = {}) {
      const qs = limit != null ? `?limit=${limit}` : '';
      const data = await unwrap(`/api/hermes/agents/${agentId}/memory${qs}`, { method: 'GET' });
      return data?.memories || [];
    },

    /** Append one memory. body = { kind?, name?, content, sourceRunId? }. Unwraps {memory} (201). */
    async appendMemory(agentId, { kind, name, content, sourceRunId } = {}) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/memory`, {
        method: 'POST',
        body: { kind, name, content, sourceRunId },
      });
      return data?.memory;
    },

    /** Edit a memory (PATCH). Unwraps {memory}. */
    async updateMemory(agentId, mid, patch) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/memory/${mid}`, {
        method: 'PATCH',
        body: patch,
      });
      return data?.memory;
    },

    /** Delete a memory. Returns { ok }. */
    async deleteMemory(agentId, mid) {
      return unwrap(`/api/hermes/agents/${agentId}/memory/${mid}`, { method: 'DELETE' });
    },

    /**
     * Semantic recall for a run. The backend applies the FROZEN injectionBudget
     * and returns a PRE-BUILT block ({ role:'system', content } | null) plus the
     * raw chunks + count. The adapter prepends `block` to the run messages.
     */
    async recallMemory(agentId, { query, limit } = {}) {
      const body = {};
      if (query != null) body.query = query;
      if (limit != null) body.limit = limit;
      return unwrap(`/api/hermes/agents/${agentId}/memory/recall`, { method: 'POST', body });
    },
  };
}

// ---- minted skills (P2-T03, D18) ------------------------------------------
//
// An agent turns a RESOLVED (done) run into a reusable SKILL.md in the
// SHARED `skills` table + links it per-agent. Deterministic, no LLM. The
// mint GATES surface as throws callers MUST distinguish: 409 run_not_done,
// 422 result_too_thin, 429 skill_cap (RunDetail maps each to a message).
function skillsApi(unwrap) {
  return {
    /** Mint a skill from a done run. body = { runId, name? }. Returns { skill, link } (201). */
    async mintSkill(agentId, { runId, name } = {}) {
      return unwrap(`/api/hermes/agents/${agentId}/skills/mint`, {
        method: 'POST',
        body: { runId, name },
      });
    },

    /** List an agent's minted skills (newest first). Unwraps {skills}. */
    async listSkills(agentId) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/skills`, { method: 'GET' });
      return data?.skills || [];
    },
  };
}

// ---- per-agent CRON schedules (P3-T03a, D19) ------------------------------
//
// A schedule = { id, agentId, cron, task, timezone, enabled, nextRunAt,
// lastRunAt, createdAt, updatedAt }. The minute-tick scheduler fires due
// schedules (runDueHermesAgents) — these routes only manage the rows.
// An unparseable cron surfaces as a throw with status 400, code
// 'invalid_cron' (distinct from 'invalid_schedule' for a bad task), so the
// SchedulePanel can render the cron error inline.
function schedulesApi(unwrap) {
  return {
    /** List an agent's schedules. Unwraps {schedules}. */
    async listSchedules(agentId) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/schedules`, { method: 'GET' });
      return data?.schedules || [];
    },

    /** Create a schedule. body = { cron, task, timezone? }. Unwraps {schedule} (201). */
    async createSchedule(agentId, { cron, task, timezone } = {}) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/schedules`, {
        method: 'POST',
        body: { cron, task, timezone },
      });
      return data?.schedule;
    },

    /** Edit a schedule (PATCH). patch = { cron?, task?, timezone?, enabled? }. Unwraps {schedule}. */
    async updateSchedule(agentId, sid, patch) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/schedules/${sid}`, {
        method: 'PATCH',
        body: patch,
      });
      return data?.schedule;
    },

    /** Delete a schedule. Returns { ok }. */
    async deleteSchedule(agentId, sid) {
      return unwrap(`/api/hermes/agents/${agentId}/schedules/${sid}`, { method: 'DELETE' });
    },
  };
}

// ---- runner presence (E1-WS2) + continuous "run 24h" sessions (E4) --------
function runtimeApi(unwrap) {
  return {
    /** Is the user's Mac runner online + can it drive a browser?
     *  Returns { online, lastSeen, browserAvailable, roots }. */
    async getRunner() {
      return unwrap('/api/hermes/runner', { method: 'GET' });
    },

    /** List an agent's continuous sessions. Unwraps {sessions}. */
    async listContinuous(agentId) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/continuous`, { method: 'GET' });
      return data?.sessions || [];
    },

    /** Start a continuous session. body = { goal, caps? }. Unwraps {session} (201). */
    async startContinuous(agentId, { goal, caps } = {}) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/continuous`, {
        method: 'POST',
        body: { goal, caps },
      });
      return data?.session;
    },

    /** Status of one session. Unwraps {session}. */
    async getContinuous(sid) {
      const data = await unwrap(`/api/hermes/continuous/${sid}`, { method: 'GET' });
      return data?.session;
    },

    /** Pause/stop/resume a session. patch = { state }. Unwraps {session}. */
    async patchContinuous(sid, patch) {
      const data = await unwrap(`/api/hermes/continuous/${sid}`, { method: 'PATCH', body: patch });
      return data?.session;
    },
  };
}

// ---- per-agent channels (P4-T01/T03, D20) ---------------------------------
//
// A channel routes inbound provider events (webhook/telegram/slack) to an
// agent and posts the run result back outbound. Reads are ALWAYS MASKED —
// each channel is { id, channelType, enabled, botIdentity, hasInboundSecret,
// hasOutbound, createdAt }, NEVER the inboundSecret/outboundConfig (the
// backend strips secrets + cipher). The inboundSecret is only known at
// CREATE time (the body the caller passes) — the ChannelPanel reminds the
// user to copy it before it is masked away.
//
// The inbound webhook URL the user pastes into their provider is
// `<API_BASE>/api/hermes/webhook/<channel id>` (built in the panel from the
// app's configured API origin). An invalid create (e.g. inboundSecret < 16
// chars) surfaces as a throw with status 400, code 'invalid_channel', so the
// ChannelPanel can render the error inline.
function channelsApi(unwrap) {
  return {
    /** List an agent's channels (masked). Unwraps {channels}. */
    async listChannels(agentId) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/channels`, { method: 'GET' });
      return data?.channels || [];
    },

    /**
     * Create a channel. body = { channelType, inboundSecret, outboundConfig?,
     * botIdentity? }. Unwraps the MASKED {channel} (201).
     */
    async createChannel(agentId, body) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/channels`, {
        method: 'POST',
        body,
      });
      return data?.channel;
    },

    /**
     * Edit a channel (PATCH). patch = { enabled?, channelType?, botIdentity?,
     * inboundSecret?, outboundConfig? }. Unwraps the MASKED {channel}.
     */
    async updateChannel(agentId, cid, patch) {
      const data = await unwrap(`/api/hermes/agents/${agentId}/channels/${cid}`, {
        method: 'PATCH',
        body: patch,
      });
      return data?.channel;
    },

    /** Delete a channel. Returns { ok }. */
    async deleteChannel(agentId, cid) {
      return unwrap(`/api/hermes/agents/${agentId}/channels/${cid}`, { method: 'DELETE' });
    },
  };
}

export function makeHermesApi({ fetchImpl = apiFetch } = {}) {
  const unwrap = makeUnwrap(fetchImpl);
  return {
    ...agentsApi(unwrap),
    ...runsApi(unwrap),
    ...memoryApi(unwrap),
    ...skillsApi(unwrap),
    ...schedulesApi(unwrap),
    ...runtimeApi(unwrap),
    ...channelsApi(unwrap),
  };
}

export const hermesApi = makeHermesApi();
